import { checkResponse } from "src/components/js/statics1";
import personClass from "../person/personClass";
import { person_create } from "../person/PersonFunctions";
import { v4 as uuidv4 } from "uuid";
import { sendMessageToAdminDevices } from "../system/firebaseClass";
import { authenticate } from "../auth/auth";

/**
 *
 * @param {string} googleToken
 * @returns  {Promise<{person: personClass, jwtToken: string, error: string}>}
 */
export async function fetchPersonWithGoogleToken(googleToken) {
  const personResult = await fetchPersonDataFromGoogleToken(googleToken);
  const personData = parsePersonResultToUserData(personResult);
  return personData;
}
export async function fetchPersonWithFacebookToken(facebookToken, email) {
  const personResult = await fetchPersonDataFromFacebookToken(email, facebookToken);
  const personData = parsePersonResultToUserData(personResult);
  return personData;
}
export async function fetchPersonWithMicrosoftToken(email, microsoftToken) {
  const personResult = await fetchPersonDataFromMicrosoftToken(email, microsoftToken);
  const personData = parsePersonResultToUserData(personResult);
  return personData;
}
/**
 *
 * @param {string} jwtToken
 * @returns  {Promise<{user: personClass, jwtToken: string, error: string}>}
 */
export async function fetchUserWithJwtToken(jwtToken) {
  const personResult = await fetchPersonDataFromJwtToken(jwtToken);
  const personData = parsePersonResultToUserData(personResult, false);
  return personData;
}
/**
 *
 * @param {string} email
 * @param {string} password
 * @returns  {Promise<{person: personClass, jwtToken: string, error: string}>}
 */
export async function fetchPersonWithEmailAndPassword(email, password) {
  const personResult = await fetchPersonDataFromEmailAndPassword(email, password);
  const personData = parsePersonResultToUserData(personResult);
  return personData;
}

/**
 *
 * @param {{success: boolean, error: string, data: {person: [], personSettings: [], token: string}}} personResult
 * @param {boolean} sendAdminMessage
 * @returns {{user: personClass, jwtToken: string, error: string}}
 * @description parse personResult to personData and send admin message that login has been done
 */
function parsePersonResultToUserData(personResult, sendAdminMessage = true) {
  // check personResult is null
  if (personResult == null) {
    console.log("authClass.js:getPersonByGoogleToken: personResult is null");
    return { user: null, jwtToken: null, error: "personResult is null" };
  }
  // check personResult.success
  if (!personResult.success) {
    return {
      user: null,
      jwtToken: null,
      error: personResult.error ? personResult.error : personResult,
    };
  }
  // set person
  const user = new personClass();
  const personData = personResult.data.person[0];
  const jwtToken = personResult.data.token;
  const personId = personData.personId;
  user.dontAsks.personId = personId;
  user.personId = personData.personId;
  user.personFirstName = personData.personFirstName;
  user.personLastName = personData.personLastName;
  user.personPhone = personData.personPhone;
  user.personEmail = personData.personEmail;
  user.personMemo = personData.personMemo;
  user.lastLoginTime = personData.lastLoginTime;
  user.darkMode = personData.darkMode;
  user.ownerAsiakasId = personData.ownerAsiakasId;
  user.settings.settings = [];
  // set personSettings
  const personSettings = personResult.data.personSettings;
  if (personSettings != null || personSettings.length == 0)
    for (let i = 0; i < personSettings.length; i++) {
      const personSetting = personSettings[i];
      user.settings.settings.push(personSetting);
    }
  user.settings.token = jwtToken;
  user.settings.personId = personId;
  // set asiakasList
  user.asiakasList = personResult.data.asiakasList;
  user.asiakasListIsFetched = true;
  // set asiakasList settings
  const settingsList = personResult.data.personSettings;

  for (let j = 0; j < user.asiakasList.length; j++) {
    const asiakas = user.asiakasList[j];
    asiakas.settings = [];
    asiakas.personIsTarjousAdmin = false;
    asiakas.personIsAsiakasAdmin = false;
    for (let i = 0; i < settingsList.length; i++) {
      const setting = settingsList[i];
      if (setting.asiakasId == asiakas.asiakasId) {
        if (setting.asiakasPersonSettingTypeId === 1) asiakas.personIsTarjousAdmin = true;
        if (setting.asiakasPersonSettingTypeId === 2) asiakas.personIsAsiakasAdmin = true;
        user.asiakasList[j].settings.push(setting);
      }
    }
  }

  user.isLoaded = true;

  // send admin message
  if (sendAdminMessage)
    sendMessageToAdminDevices(
      "Login ",
      "Login from " + user.personEmail + " at " + new Date().toLocaleString(),
      null,
      jwtToken,
      "login"
    );

  return { user: user, jwtToken: jwtToken, error: null };
}

/**
 *
 * @param {string} token
 * @returns {Promise<{success: boolean, error: string, data: {person: [], personSettings: [], token: string}}>}
 * @description fetch person data from google token
 * @throws null
 */
async function fetchPersonDataFromGoogleToken(token) {
  try {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    };
    const response = await fetch(
      import.meta.env.VITE_SERVER_URL + "/api/auth/login/loginWithGoogleToken",
      requestOptions
    );
    const cResponse = await checkResponse(response, "authClass.js:getPersonByGoogleToken");
    return cResponse;
  } catch (error) {
    console.log("authClass.js:loginWithGoogleToken: error: ", error);
    return null;
  }
}
async function fetchPersonDataFromFacebookToken(email, facebookToken) {
  try {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: email, facebookToken: facebookToken }),
    };
    const response = await fetch(
      import.meta.env.VITE_SERVER_URL + "/api/auth/login/loginWithFacebookToken",
      requestOptions
    );
    const cResponse = await checkResponse(response, "loginFunctions.js:loginWithFacebookToken");
    return cResponse;
  } catch (error) {
    console.log("loginFunctions.js:loginWithFacebookToken: error: ", error);
    return null;
  }
}
async function fetchPersonDataFromMicrosoftToken(email, microsoftToken) {
  try {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: email, token: microsoftToken }),
    };
    const response = await fetch(
      import.meta.env.VITE_SERVER_URL + "/api/auth/login/loginWithMicrosoftToken",
      requestOptions
    );
    const cResponse = await checkResponse(response, "loginFunctions.js:loginWithMicrosoftToken");
    return cResponse;
  } catch (error) {
    console.log("loginFunctions.js:loginWithMicrosoftToken: error: ", error);
    return null;
  }
}
/**
 *
 * @param {string} token
 * @returns {Promise<{success: boolean, error: string, data: {person: [], personSettings: [], token: string}}>}
 * @description fetch person data from jwt token
 * @throws null
 */
async function fetchPersonDataFromJwtToken(token) {
  try {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    };
    const response = await fetch(
      import.meta.env.VITE_SERVER_URL + "/api/auth/login/loginWithJwtToken",
      requestOptions
    );
    const cResponse = await checkResponse(response, "authClass.js:getPersonByJwtToken");
    return cResponse;
  } catch (error) {
    console.log("authClass.js:fetchPersonDataFromJwtToken: error: ", error);
    return null;
  }
}
/**
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{success: boolean, error: string, data: {person: [], personSettings: [], token: string}}>}
 * @description fetch person data from email and password
 * @throws null
 */
async function fetchPersonDataFromEmailAndPassword(email, password) {
  try {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": 360,
      },
      body: JSON.stringify({ email, password }),
    };
    const response = await fetch(
      import.meta.env.VITE_SERVER_URL + "/api/auth/login/loginWithEmailAndPassword",
      requestOptions
    );
    const cResponse = await checkResponse(response, "authClass.js:getPersonByEmailAndPassword");
    return cResponse;
  } catch (error) {
    console.log("authClass.js:loginWithEmailAndPassword: error: ", error);
    return null;
  }
}

export async function registerWithEmailAndPassword(etunimi, sukunimi, email, password) {
  const returnMessage = { success: false, error: null, severity: "error" };
  etunimi = etunimi.trim();
  sukunimi = sukunimi.trim();
  email = email.trim();
  password = password.trim();
  // create a GUID password if password is empty
  if (password.length == 0) {
    password = uuidv4();
  }
  if (etunimi.length < 2 || sukunimi.length < 2) {
    returnMessage.error = "Etunimi tai sukunimi puuttuu.";
    returnMessage.severity = "warning";
    return returnMessage;
  }
  if (!email.includes("@") || !email.includes(".")) {
    returnMessage.error = "Sähköposti ei ole oikeassa muodossa.";
    returnMessage.severity = "warning";
    return returnMessage;
  }
  const createResult = await person_create(email, email, etunimi, sukunimi, password, null);
  if (createResult.success == false) {
    returnMessage.error = createResult.message;
    returnMessage.severity = "error";
    return returnMessage;
  }
  const personResult = await fetchPersonWithEmailAndPassword(email, password);
  if (personResult.success == false) {
    returnMessage.error = personResult.message;
    returnMessage.severity = "error";
    return returnMessage;
  }
  return personResult;
}
export async function savePersonPasswordToDB(personId, password, token) {
  const addr = "/api/person/setPassword/";
  const response = await fetch(import.meta.env.VITE_SERVER_URL + addr, {
    method: "POST",
    headers: {
      "x-access-token": `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      personId: personId,
      password: password,
    }),
  });
  return response.ok;
}

export async function generatePasswordResetLink(personId, token) {
  try {
    const myToken = uuidv4();
    const requestOptions = {
      method: "POST",
      headers: {
        "x-access-token": `${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        personId: personId,
        resetUrl: "https://betoni.online/resetPw/",
        token: myToken,
      }),
    };
    const response = await fetch(
      import.meta.env.VITE_SERVER_URL + "/api/auth/generatePasswordResetLink/" + personId,
      requestOptions
    );
    if (!response.ok) return null;
    const cResponse = await checkResponse(response, "mailClass:sendPasswordResetEmail");
    if (cResponse.success) return cResponse.data.resetUrl;
    return null;
  } catch (e) {
    console.log(e);
    return null;
  }
}

export async function sendPasswordResetEmail(email) {
  try {
    const token = uuidv4();
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        resetUrl: "https://betoni.online/resetPw/",
        token: token,
      }),
    };
    const response = await fetch(
      import.meta.env.VITE_SERVER_URL + "/api/mail/sendPasswordResetEmail",
      requestOptions
    );
    if (!response.ok) return null;
    const cResponse = await checkResponse(response, "mailClass:sendPasswordResetEmail");
    return cResponse;
  } catch (e) {
    console.log(e);
    return null;
  }
}

export async function fetchAccessToken(email, personId) {
  if (email == null || email == "") return null;
  const authResponse = await authenticate(email, personId);
  return authResponse.token;
}

/**
 *
 * @returns
 */
export async function fetchTempAccessToken() {
  // return accessToken that lives for 3 minutes
  return await fetchAccessToken("tempAccessToken@betoni.online", null);
}
